import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { CHANNEL_NAMES } from "../../components/notificationCenter/channelText";
import {
  SA,
  SaCard,
  SaPageHeader,
  StatusPill,
  TONES,
  Tone,
  saTableBodySx,
  saTableHeadSx,
} from "../../components/superadmin/saUi";
import { PriceChangesTable, PriceForm, PriceHistoryTable } from "../../components/superadmin/PriceEditor";
import {
  ChannelPricing,
  PaidChannel,
  PricingOverview,
  deleteDefaultPriceChange,
  formatUnitPrice,
  getPricingOverview,
  setDefaultPrice,
} from "../../services/notificationCenterService";

const errorMessage = (err: any, fallback: string) => {
  const message = err?.response?.data?.message;
  return (Array.isArray(message) ? message[0] : message) || fallback;
};

const LOOK: Record<PaidChannel, { icon: React.ReactNode; tone: Tone }> = {
  SMS: { icon: <SmsOutlinedIcon />, tone: "amber" },
  WHATSAPP: { icon: <WhatsAppIcon />, tone: "green" },
};

const Fact: React.FC<{ label: string; children: React.ReactNode; tone?: Tone }> = ({ label, children, tone }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: "0.78rem", color: SA.textMuted, fontWeight: 500 }}>{label}</Typography>
    <Typography
      sx={{
        fontSize: "1.35rem",
        fontWeight: 700,
        color: tone ? TONES[tone].fg : SA.text,
        fontVariantNumeric: "tabular-nums",
        lineHeight: 1.25,
      }}
    >
      {children}
    </Typography>
  </Box>
);

const ChannelPriceCard: React.FC<{
  pricing: ChannelPricing;
  currency: string;
  onSet: (channel: PaidChannel, value: Parameters<typeof setDefaultPrice>[1]) => Promise<void>;
}> = ({ pricing, currency, onSet }) => {
  const { current, upcoming } = pricing;
  const margin = current?.price != null && current.cost != null ? current.price - current.cost : null;
  const look = LOOK[pricing.channel];

  return (
    <SaCard
      title={`${CHANNEL_NAMES[pricing.channel]} default price`}
      subtitle="Every organization pays this unless it has its own price."
      icon={look.icon}
      tone={look.tone}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 2, mb: 2 }}>
        <Fact label="Price now">{current?.price != null ? formatUnitPrice(current.price, currency) : "Not set"}</Fact>
        <Fact label="Our cost">{formatUnitPrice(current?.cost, currency)}</Fact>
        <Fact label="Margin" tone={margin === null ? undefined : margin < 0 ? "red" : "green"}>
          {formatUnitPrice(margin, currency)}
        </Fact>
      </Box>
      {current && (
        <Typography sx={{ fontSize: "0.82rem", color: SA.textMuted, mt: -1, mb: 2 }}>
          Since {format(new Date(current.from), "d MMM yyyy, HH:mm")}
        </Typography>
      )}
      {upcoming && (
        <Alert severity="warning" icon={false} sx={{ mb: 2, borderRadius: "10px" }}>
          From <strong>{format(new Date(upcoming.from), "d MMM yyyy")}</strong>:{" "}
          <strong>{formatUnitPrice(upcoming.price, currency)}</strong> (our cost {formatUnitPrice(upcoming.cost, currency)}).
        </Alert>
      )}

      <Typography sx={{ fontWeight: 650, fontSize: "0.9rem", color: SA.text, mb: 1 }}>Set a new price</Typography>
      <PriceForm
        withCost
        currency={currency}
        initialCost={current?.cost ?? null}
        onSubmit={(value) => onSet(pricing.channel, { price: value.price!, cost: value.cost, effectiveFrom: value.effectiveFrom, note: value.note })}
      />

      <Typography sx={{ fontWeight: 650, fontSize: "0.9rem", color: SA.text, mt: 3, mb: 1 }}>History</Typography>
      <PriceHistoryTable history={pricing.history} currency={currency} />
    </SaCard>
  );
};

/**
 * What SMS and WhatsApp messages cost organizations. Prices are a history:
 * every message keeps the price it was sent at, so changing one never changes
 * past totals.
 */
const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<PricingOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getPricingOverview()
      .then(setData)
      .catch((err) => setError(errorMessage(err, "Could not load prices.")));
  }, []);
  useEffect(load, [load]);

  const onSet = async (channel: PaidChannel, value: Parameters<typeof setDefaultPrice>[1]) => {
    try {
      setData(await setDefaultPrice(channel, value));
      toast.success(value.effectiveFrom ? "Price change scheduled." : "Price saved.", { position: "top-right" });
    } catch (err) {
      toast.error(errorMessage(err, "Could not save the price."), { position: "top-right" });
      throw err;
    }
  };

  return (
    <Box>
      <SaPageHeader
        title="Message prices"
        subtitle="What organizations pay per delivered SMS and WhatsApp message, and what each costs us."
        icon={<SellOutlinedIcon />}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {!data && !error && <CircularProgress size={26} />}

      {data && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon />}
            sx={{ borderRadius: SA.radius, border: `1px solid ${TONES.blue.border}`, bgcolor: TONES.blue.bg, color: TONES.blue.fg }}
          >
            Only delivered messages are charged. Each message saves the price it was sent at, so a new price only
            applies to messages sent after it starts. Login and password reset codes are always free to the
            organization; their cost to us still shows on the Messages page.
          </Alert>

          <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" } }}>
            {data.channels.map((channel) => (
              <ChannelPriceCard key={channel.channel} pricing={channel} currency={data.currency} onSet={onSet} />
            ))}
          </Box>

          <SaCard
            title="Organizations with their own price"
            subtitle="Set or remove one from the organization's Messages section."
            icon={<ApartmentOutlinedIcon />}
            tone="violet"
            flush
          >
            {data.organizations.length === 0 ? (
              <Typography sx={{ p: 2.5, fontSize: "0.88rem", color: SA.textMuted }}>
                None. Every organization pays the default price.
              </Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 560 }}>
                  <TableHead sx={saTableHeadSx}>
                    <TableRow>
                      <TableCell>Organization</TableCell>
                      {data.channels.map((c) => (
                        <TableCell key={c.channel} align="right">
                          {CHANNEL_NAMES[c.channel]}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody sx={saTableBodySx}>
                    {data.organizations.map((org) => (
                      <TableRow
                        key={org.organizationId}
                        hover
                        onClick={() => navigate(`/superadmin/organizations/${org.organizationId}?tab=messages`)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>{org.organizationName}</TableCell>
                        {org.channels.map((c) => (
                          <TableCell key={c.channel} align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", justifyContent: "flex-end" }}>
                              <span>{formatUnitPrice(c.price, data.currency)}</span>
                              {c.source === "ORGANIZATION" ? (
                                <StatusPill tone="blue" dot={false}>
                                  Own
                                </StatusPill>
                              ) : (
                                <StatusPill tone="slate" dot={false}>
                                  Default
                                </StatusPill>
                              )}
                              {c.scheduled && (
                                <StatusPill tone="amber">
                                  {c.scheduled.price === null ? "Default" : formatUnitPrice(c.scheduled.price, data.currency)} from{" "}
                                  {format(new Date(c.scheduled.from), "d MMM")}
                                </StatusPill>
                              )}
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </SaCard>

          <SaCard
            title="Default price changes"
            subtitle="Every default price that was set. A change that hasn't started yet can be cancelled."
            icon={<HistoryOutlinedIcon />}
            tone="slate"
          >
            <PriceChangesTable
              withCost
              changes={data.changes}
              currency={data.currency}
              channelNames={CHANNEL_NAMES}
              onDelete={async (change) => {
                try {
                  setData(await deleteDefaultPriceChange(change.id));
                  toast.success("Scheduled change cancelled.", { position: "top-right" });
                } catch (err) {
                  toast.error(errorMessage(err, "Could not cancel it."), { position: "top-right" });
                }
              }}
            />
          </SaCard>
        </Box>
      )}
    </Box>
  );
};

export default Pricing;
